/**
 * _onboarding_beacon.js — reports onboarding from the browser into
 * activity_events (application/web/views/api.py). Two reporters:
 *
 * - reportOnboarding: what a first-run business sees and does on screen, to
 *   /api/onboarding-events (application/services/onboarding_events.py). The
 *   server keeps these only while the business has no quote yet and answers
 *   `first_run: false` once it has one. That answer is remembered per
 *   business in localStorage, so an established account stops sending after
 *   one request on each device instead of reporting every form it ever opens.
 * - reportProgress: one person's walkthrough and checklist steps, to
 *   /api/onboarding-progress (application/services/onboarding_progress.py).
 *   Kept for everyone, demo users included, with no first-run cut-off, so it
 *   shares none of reportOnboarding's limits. The events and the fields each
 *   carries are ONBOARDING_FIELDS in application/models/public/activity_event.py.
 *
 * keepalive lets an event reported while the page is being left (a Turbo
 * visit with a create sheet open, a checklist link followed) still reach the
 * server. Reporting never throws and never blocks the caller.
 *
 * currentRun reads back where the person's walkthrough run is, from the
 * events reportProgress stored (/api/walkthrough-run).
 *
 * seeClientView is the one that waits for an answer: the owner's client
 * preview asks whether this is the person's first client view
 * (application/web/views/client_preview.py), and only a yes plays the peak.
 */

const ENDPOINT = "/api/onboarding-events";
const PROGRESS_ENDPOINT = "/api/onboarding-progress";
const RUN_ENDPOINT = "/api/walkthrough-run";

function businessId() {
    return document.body?.dataset.currentBusinessId || "";
}

function doneKey() {
    return `chippie_onboarding_done_${businessId()}`;
}

function isDone() {
    try {
        return localStorage.getItem(doneKey()) === "1";
    } catch {
        return false;
    }
}

function markDone() {
    try {
        localStorage.setItem(doneKey(), "1");
    } catch {
        // Storage unavailable: the server keeps dropping the events instead.
    }
}

function csrfToken() {
    return document.querySelector("meta[name=csrf-token]")?.getAttribute("content") || "";
}

/**
 * Report one screen event; `form` names the create form (quote, job, client)
 * for the form events and is left out otherwise.
 */
export function reportOnboarding(event, form) {
    if (!businessId() || isDone()) return;
    fetch(ENDPOINT, {
        method: "POST",
        keepalive: true,
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken() },
        body: JSON.stringify(form ? { event, form } : { event }),
    })
        .then((response) => (response.ok ? response.json() : null))
        .then((answer) => { if (answer && answer.first_run === false) markDone(); })
        .catch(() => {});
}

/**
 * Record a client view opened from `via` at `url` and answer
 * `{ recorded, first }`: whether the server stored it, and whether it was the
 * person's first. Never throws; with no answer, nothing was recorded.
 */
export async function seeClientView(url, via) {
    try {
        const response = await fetch(url, {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken() },
            body: JSON.stringify({ via }),
        });
        if (!response.ok) return { recorded: false, first: false };
        const answer = await response.json();
        return { recorded: true, first: answer.first === true };
    } catch {
        return { recorded: false, first: false };
    }
}

/**
 * The person's walkthrough run under way here, as the server keeps it
 * (application/services/walkthrough_progress.py run_now): `{ step, pending,
 * quote_id, job_id, invoice_id }`, or null with none or no answer. Never throws.
 */
export async function currentRun() {
    try {
        const response = await fetch(RUN_ENDPOINT, { credentials: "same-origin", headers: { Accept: "application/json" } });
        if (!response.ok) return null;
        return (await response.json()).run ?? null;
    } catch {
        return null;
    }
}

/** Report one onboarding progress event with exactly the fields it carries. */
export function reportProgress(event, fields = {}) {
    fetch(PROGRESS_ENDPOINT, {
        method: "POST",
        keepalive: true,
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken() },
        body: JSON.stringify({ event, ...fields }),
    }).catch(() => {});
}
